import { CoreButton, CoreCurrencyIcon, MMButton } from '@components'
import ArrowDownIcon from '@images/icons/chevron-down-black.svg?react'
import ArrowUpIcon from '@images/icons/chevron-up-black.svg?react'
import AddAccountModal from '@views/accounts/AddAccountModal/AddAccountModal'
import cn from 'classnames'
import { GlobalContext, isValidArray, tr, useOutsideClick } from 'mmfintech-commons'
import { useCallback, useContext, useEffect, useState } from 'react'
import { CoreInput } from '../CoreInput'
import { OtherCurrencies } from './OtherCurrencies'
import { SelectionElement } from './SelectionElement'
import type { SelectionProps } from './selectionTypes'
import { filterOptions, SelectedIcon, selectedLabel } from './selectionUtils'

import './coreSelect.scss'

export const CoreSelect = (props: SelectionProps) => {
  const {
    LeftIcon,
    label,
    name,
    type = 'default',
    options,
    onChange,
    required,
    error,
    hint,
    className,
    hideCaption,
    value,
    disabled,
    loading,
    isMultiply = false,
    allAccounts = false,
    isAccountsLoading = false,
    enableAccountCreation = false,
    extraOptions,
    currencyOptions,
    CreateComponent,
    enablePrefillOption = true,
    readOnlyInput = false,
    clearLabel,
    dropdownHint,
    noOptionsMessage,
    noOptionsAction
  } = props

  const [inputFocused, setInputFocused] = useState<boolean>(false)
  const [filterString, setFilterString] = useState<string>('')
  const [filteredOptions, setFilteredOptions] = useState<any[]>(options || [])
  const [selectedOption, setSelectedOption] = useState<any>(null)
  const [selectedOptions, setSelectedOptions] = useState<any[]>([])
  const [offsetErrorHeight, setOffsetErrorHeight] = useState<number>(0)
  const [offsetHintHeight, setOffsetHintHeight] = useState<number>(0)
  const [createdAccountId, setCreatedAccountId] = useState<any>(null)
  const [blockBlur, setBlockBlur] = useState<boolean>(false)

  const inputRef = useOutsideClick(() => {
    setInputFocused(false)
  })
  const { modalShow } = useContext(GlobalContext)

  const extractValues = useCallback(
    (options: any[]): string[] => (Array.isArray(options) ? options.map(entry => entry.value) : []),
    []
  )

  const getSelectedValues = useCallback(() => extractValues(selectedOptions), [selectedOptions])

  const isValueSelected = useCallback(
    (value: string) => (Array.isArray(selectedOptions) ? selectedOptions.some(entry => entry.value === value) : false),
    [selectedOptions]
  )

  // When clearLabel is provided, disable auto-prefill to prevent
  // automatically selecting the first option after clearing
  const shouldEnablePrefill = clearLabel ? false : enablePrefillOption

  const handleAddAccountClick = () => {
    modalShow({
      content: <AddAccountModal setCreatedAccount={setCreatedAccountId} currencyOptions={currencyOptions} />,
      header: tr('FRONTEND.ACCOUNTS.CREATE.TITLE', 'Add New Account'),
      options: { closeOnClickOutside: true }
    })
  }

  const removeFromSelection = value => {
    const rest = selectedOptions.filter(option => option.value !== value)
    setSelectedOptions(rest)
    onChange(name, [...extractValues(rest)])
  }

  useEffect(() => {
    setFilteredOptions(filterOptions(type, options, filterString))
  }, [filterString, options])

  useEffect(() => {
    setFilterString('')
    if (inputRef?.current) {
      const error = inputRef.current.querySelector('.core-input-error') as HTMLElement
      const hint = inputRef.current.querySelector('.core-input-hint') as HTMLElement
      setOffsetErrorHeight(error ? error.offsetHeight : 0)
      setOffsetHintHeight(hint ? hint.offsetHeight : 0)
    }

    // onBlur case
    if (!inputFocused && !selectedOption) {
      setFilterString('')
      if (!filteredOptions[0]) {
        setSelectedOption(null)
        return
      }

      if (shouldEnablePrefill) {
        if (filteredOptions[0]?.options) {
          const findOptions = filteredOptions.reduce((acc, options) => {
            if (isValidArray(options?.options)) {
              acc.push(options)
            }
            return acc
          }, [])

          if (isValidArray(findOptions)) {
            onChange?.(
              name,
              isMultiply
                ? Array.from(new Set([...getSelectedValues(), findOptions[0]?.options?.[0].value]))
                : findOptions[0]?.options?.[0].value
            )
          } else {
            setSelectedOption(null)
          }
        } else {
          if (filteredOptions.length === 1 && typeof onChange === 'function') {
            const option = filteredOptions[0]
            if ((type === 'country' || type === 'default' || type === 'status') && 'value' in option) {
              onChange(name, isMultiply ? Array.from(new Set([...getSelectedValues(), option.value])) : option.value)
            } else {
              onChange(name, isMultiply ? Array.from(new Set([...selectedOptions, option])) : option)
            }
          }
        }
      }
    }
  }, [inputFocused])

  useEffect(() => {
    if (isValidArray(options) && !isValidArray(filteredOptions)) {
      setFilteredOptions(options)
    }
  }, [options])

  useEffect(() => {
    if (type === 'account') {
      const foundAccount = filteredOptions?.find(account => account.id === createdAccountId)
      if (foundAccount) {
        setSelectedOption(foundAccount)
        onChange(name, foundAccount)
      }
    }
  }, [createdAccountId, filteredOptions])

  useEffect(() => {
    if (!value || value?.length === 0) {
      setSelectedOption(null)
      setSelectedOptions([])
      return
    }

    if (isMultiply) {
      setSelectedOptions(options.filter(option => value?.includes(option.value)))
    } else {
      if (typeof value === 'string' || typeof value === 'number') {
        const foundOption = options.find(option => String(option.value) === String(value))
        setSelectedOption(foundOption || null)
        return
      }
    }
    setSelectedOption(value)
  }, [value, options])

  return (
    <div ref={inputRef} className={cn(className, 'core-selection-wrapper', { disabled })}>
      {!inputFocused && (selectedOption || isValidArray(selectedOptions)) ? (
        <div
          className='selected-content'
          onClick={ev => {
            ev.preventDefault()
            ev.stopPropagation()
            !isMultiply && setInputFocused(true)
          }}
        >
          {isMultiply ? (
            <SelectionElement
              data-test='selected-content'
              selectedList={selectedOptions}
              type='multiselect'
              isCountryType={type === 'country'}
              setInputFocused={setInputFocused}
              isAccountsLoading={isAccountsLoading}
              isSelected
              error={!inputFocused ? error : null}
              hideCaption={hideCaption}
              removeFromSelection={removeFromSelection}
            />
          ) : (
            <SelectionElement
              data-test='selected-content'
              data={selectedOption}
              type={type}
              isCountryType={type === 'country'}
              setInputFocused={setInputFocused}
              isAccountsLoading={isAccountsLoading}
              isSelected
              error={!inputFocused ? error : null}
              hideCaption={hideCaption}
            />
          )}
        </div>
      ) : (
        <CoreInput
          readOnly={readOnlyInput}
          LeftIcon={
            (selectedOption && type !== 'default') || (type === 'account' && isAccountsLoading) ? (
              selectedOption && type === 'currency' ? (
                inputFocused ? null : (
                  <CoreCurrencyIcon
                    currency={typeof selectedOption === 'string' ? selectedOption : selectedOption?.value}
                    size='32px'
                    circle
                  />
                )
              ) : (
                <SelectedIcon isLoading={isAccountsLoading} type={type} selectedOption={selectedOption} />
              )
            ) : (
              LeftIcon
            )
          }
          label={selectedOption ? selectedLabel(type, selectedOption) : label}
          RightIcon={
            loading ? null : // <LoadingButton
            //   loadButtonClass='secondary-normal-loading-button'
            //   size={'normal'}
            //   variation='tertiary'
            //   style={{ backgroundColor: 'transparent' }}
            // />
            inputFocused ? (
              <ArrowUpIcon onClick={() => setInputFocused(false)} />
            ) : (
              <ArrowDownIcon onClick={() => setInputFocused(true)} />
            )
          }
          enableAutofill={false}
          setFocus={setInputFocused}
          value={selectedOption?.value ? '' : filterString}
          name={name}
          onChange={(_: string, value: string) => setFilterString(value)}
          focused={inputFocused}
          required={required}
          blockBlur={blockBlur}
          error={error}
          disabled={disabled}
          hint={hint}
          onKeyDown={e => {
            if (e.key === 'Enter' && filteredOptions.length === 1) {
              if ((type === 'country' || type === 'default' || type === 'status') && 'value' in filteredOptions[0]) {
                onChange?.(name, filteredOptions[0]?.value)
              } else {
                onChange?.(name, filteredOptions[0])
              }
              setSelectedOption(filteredOptions[0])
              setInputFocused(false)
            }
          }}
        />
      )}

      {inputFocused && (
        <div
          onMouseDown={() => {
            setBlockBlur(true)
          }}
          onMouseOut={() => setBlockBlur(false)}
          style={{ marginTop: -offsetErrorHeight + -offsetHintHeight }}
          className='core-selection-data-container'
        >
          {dropdownHint && <div className='dropdown-hint'>{dropdownHint}</div>}

          {type === 'account' && allAccounts && (
            <SelectionElement
              data-test='selected-content'
              key={'all-accounts'}
              data={'all-accounts'}
              type='account'
              isAccountsLoading={isAccountsLoading}
              hideCaption={false}
              onSelect={() => {
                onChange?.(name, null)
                setSelectedOption(null)
                setInputFocused(false)
              }}
              // error={error}
            />
          )}

          {clearLabel && (
            <SelectionElement
              className='clear-label'
              data-test='clear'
              key={'clear'}
              data={clearLabel}
              type='default'
              hideCaption={false}
              onSelect={() => {
                onChange?.(name, null)
                setSelectedOption(null)
                setInputFocused(false)
              }}
              // error={error}
            />
          )}

          {isValidArray(options)
            ? filteredOptions.map((option, key) => {
                // const inSelectedList = selectedListOption.some(element => element.value === option.value)
                return option.options && !isValidArray(option.options) ? null : option?.options ? (
                  <div key={key}>
                    <div className='inner-dropdown-category-title'>{option.label}</div>
                    {option.options.map((innerOption: any, index: number) => {
                      // const innerInSelectedList = selectedListOption.some(
                      //   element => element.value === innerOption.value
                      // )
                      return (
                        <SelectionElement
                          data-test='selected-content'
                          key={`${key}-${index}`}
                          selectedList={selectedOptions}
                          data={innerOption}
                          type={type}
                          inSelectedList={isValueSelected(innerOption.value)}
                          hideCaption={hideCaption}
                          onSelect={selectedData => {
                            // const innerInSelectedList = selectedListOption.some(
                            //   element => element.value === selectedData.value
                            // )
                            if (!isValueSelected(selectedData.value)) {
                              if (
                                (type === 'country' || type === 'default' || type === 'status') &&
                                'value' in selectedData
                              ) {
                                onChange?.(
                                  name,
                                  isMultiply ? [...getSelectedValues(), selectedData.value] : selectedData?.value
                                )
                              } else {
                                onChange?.(name, isMultiply ? [...selectedOptions, selectedData] : selectedData)
                              }
                              if (isMultiply) {
                                setSelectedOptions(prevState => [...prevState, selectedData])
                              } else {
                                setSelectedOption(selectedData)
                              }
                              setInputFocused(false)
                            }
                          }}
                        />
                      )
                    })}
                  </div>
                ) : (
                  <SelectionElement
                    data-test='selected-content'
                    key={key}
                    selectedList={selectedOptions}
                    data={option}
                    type={type}
                    inSelectedList={isValueSelected(option.value)}
                    hideCaption={hideCaption}
                    onSelect={selectedData => {
                      // const inSelectedList = selectedListOption.some(element => element.value === selectedData.value)
                      if (!isValueSelected(selectedData.value)) {
                        if (
                          (type === 'country' || type === 'default' || type === 'status') &&
                          'value' in selectedData
                        ) {
                          onChange?.(
                            name,
                            isMultiply ? [...getSelectedValues(), selectedData.value] : selectedData?.value
                          )
                        } else {
                          onChange?.(name, isMultiply ? [...selectedOptions, selectedData] : selectedData)
                        }
                        if (isMultiply) {
                          setSelectedOptions(prevState => [...prevState, selectedData])
                        } else {
                          setSelectedOption(selectedData)
                        }
                        setInputFocused(false)
                      }
                    }}
                  />
                )
              })
            : !isAccountsLoading && (
                <div className='core-selections-no-options'>
                  <span>{noOptionsMessage || tr('FRONTEND.SELECT.NO_OPTIONS', 'No valid options')}</span>
                  {noOptionsAction && (
                    <MMButton
                      type='button'
                      size='small'
                      caption={noOptionsAction.label}
                      onClick={noOptionsAction.onClick}
                    />
                  )}
                </div>
              )}

          {enableAccountCreation && (
            <div className='add-account-button'>
              <CoreButton
                variation='primary'
                fullWidth
                size='normal'
                text={tr('FRONTEND.ACCOUNTS.CREATE.TITLE', 'Add New Account')}
                onClick={handleAddAccountClick}
              />
            </div>
          )}

          {!isAccountsLoading && isValidArray(extraOptions) && (
            <OtherCurrencies currencies={extraOptions} onClick={onChange} setInputFocused={setInputFocused} />
          )}

          {CreateComponent && (
            <div
              key='create-select-component'
              className='create-select-component'
              onClick={() => setInputFocused(false)}
            >
              <CreateComponent filterString={filterString} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
